import React, { useState } from 'react';
import { CreditCard, Link as LinkIcon, Copy, Check, Loader2, AlertCircle } from 'lucide-react';

const PaymentGenerator: React.FC = () => {
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generateLink = async () => {
    if (!invoiceId || !amount || isNaN(Number(amount))) {
      setError('Please provide a valid Invoice ID and Amount.');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedUrl('');
    setCopied(false);

    try {
      const response = await fetch('/.netlify/functions/create_payment_link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount),
          invoice_id: invoiceId
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to generate link');
      }

      const data = await response.json();
      setGeneratedUrl(data.url);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'System error. Contact engineering.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-8 max-w-2xl mx-auto shadow-sm">
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-zinc-900">Payment Link Generator</h2>
        <p className="text-zinc-500 text-sm">
          Create a secure, one-time payment link via Stripe. The generated link will automatically deactivate after a single successful transaction.
        </p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Invoice Reference</label>
            <input
              type="text"
              placeholder="GF-2024-001"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              className="w-full bg-zinc-50 border border-zinc-200 rounded-lg py-3 px-4 text-zinc-900 focus:outline-none focus:border-[#D4AF37] transition-colors"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Amount (GBP £)</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">£</span>
              <input
                type="number"
                placeholder="250.00"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-zinc-50 border border-zinc-200 rounded-lg py-3 pl-8 pr-4 text-zinc-900 focus:outline-none focus:border-[#D4AF37] transition-colors"
              />
            </div>
          </div>
        </div>

        <button
          onClick={generateLink}
          disabled={isGenerating || !invoiceId || !amount}
          className="w-full bg-[#D4AF37] text-black font-bold py-4 rounded-lg flex items-center justify-center gap-2 hover:bg-[#C19A2E] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          {isGenerating ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Securing Link...
            </>
          ) : (
            <>
              <LinkIcon size={18} />
              Generate Single-Use Link
            </>
          )}
        </button>

        {error && (
          <div className="p-4 rounded-lg bg-red-50 text-red-700 border border-red-200 flex items-start gap-3 text-sm">
            <AlertCircle className="shrink-0 mt-0.5" size={16} />
            {error}
          </div>
        )}

        {generatedUrl && (
          <div className="space-y-3 animate-in fade-in zoom-in-95 duration-300">
            <label className="text-xs font-semibold uppercase tracking-wider text-[#D4AF37]">Secure Payment URL</label>
            <div className="flex gap-2">
              <div className="flex-1 bg-zinc-50 border border-[#D4AF37]/30 rounded-lg py-3 px-4 text-sm text-zinc-600 overflow-hidden text-ellipsis whitespace-nowrap">
                {generatedUrl}
              </div>
              <button
                onClick={copyToClipboard}
                className="bg-zinc-100 p-3 rounded-lg hover:bg-zinc-200 transition-colors flex items-center justify-center"
                title="Copy to clipboard"
              >
                {copied ? <Check className="text-green-600" size={18} /> : <Copy size={18} className="text-zinc-600" />}
              </button>
            </div>
            <p className="text-[10px] text-zinc-400 italic">
              * This link is unique to Invoice {invoiceId} and will expire once the payment is completed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentGenerator;